import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Image as ImageIcon, Loader2, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ImageSwapLightbox({
  imageUrl, title, creditText = "", credit = null, altsUrl, selectUrl,
  unsplashOptionsUrl, unsplashSelectUrl, autoSearch = false,
  onClose, onSelected, allowSwap = true,
  altAspectClass = "aspect-square", testIdBase = "img",
}) {
  const [alts, setAlts] = useState(null);
  const [altsLoading, setAltsLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [uOptions, setUOptions] = useState(null);
  const [uQuery, setUQuery] = useState("");
  const [uLoading, setULoading] = useState(false);
  const [uSelecting, setUSelecting] = useState(null);

  const searchUnsplash = async (q = "") => {
    setULoading(true);
    setAlts(null);
    try {
      const r = await api.post(unsplashOptionsUrl, { query: q });
      setUOptions(r.data.options || []);
      setUQuery(r.data.query || q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unsplash search failed");
    } finally { setULoading(false); }
  };

  const pickUnsplash = async (opt) => {
    setUSelecting(opt.image_url);
    try {
      const { data } = await api.post(unsplashSelectUrl, { option: opt });
      toast.success(`Photo by ${opt.photographer} set — credit added`);
      onSelected?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set photo");
    } finally { setUSelecting(null); }
  };

  useEffect(() => {
    if (autoSearch && unsplashOptionsUrl) searchUnsplash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const genAlts = async () => {
    setAltsLoading(true);
    try {
      const r = await api.post(altsUrl);
      setAlts(r.data.alternatives || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate alternatives");
    } finally { setAltsLoading(false); }
  };

  const pickAlt = async (altId) => {
    setSelecting(altId);
    try {
      const { data } = await api.post(selectUrl, { alt_id: altId });
      toast.success("New image set");
      onSelected?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set image");
    } finally { setSelecting(null); }
  };

  // Portal to <body>: ancestors with CSS transforms (e.g. the card's
  // .lift hover effect) would otherwise trap position:fixed overlays.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-6 cursor-zoom-out overflow-y-auto"
      onClick={onClose}
      data-testid={`${testIdBase}-lightbox`}
    >
      <div className="relative max-w-[min(90vw,720px)] w-full cursor-default" onClick={(e) => e.stopPropagation()}>
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="w-full max-h-[62vh] object-contain rounded-xl border border-line shadow-2xl" />
        ) : (
          <div className="w-full rounded-xl border border-dashed border-white/25 bg-white/5 py-10 grid place-items-center" data-testid={`${testIdBase}-no-image-placeholder`}>
            <div className="text-center">
              <ImageIcon className="h-8 w-8 text-white/40 mx-auto mb-2" />
              <div className="text-sm text-white/70">No featured image yet — pick one below</div>
            </div>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{title}</div>
            {credit?.photographer ? (
              <div className="text-xs text-white/85" data-testid={`${testIdBase}-credit-links`}>
                Photo by{" "}
                <a href={credit.photographer_url} target="_blank" rel="noreferrer" className="underline hover:text-white">{credit.photographer}</a>
                {" "}on{" "}
                <a href={credit.unsplash_url || "https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral"} target="_blank" rel="noreferrer" className="underline hover:text-white">Unsplash</a>
              </div>
            ) : creditText ? (
              <div className="text-xs text-white/70">{creditText}</div>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {unsplashOptionsUrl && (
              <button
                onClick={() => searchUnsplash()}
                disabled={uLoading}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-sky-500/90 text-white font-bold hover:bg-sky-500 transition disabled:opacity-60"
                data-testid={`${testIdBase}-unsplash-grid-btn`}
              >
                {uLoading
                  ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching Unsplash…</>)
                  : (<><ImageIcon className="h-3.5 w-3.5" /> Unsplash photos</>)}
              </button>
            )}
            {allowSwap && imageUrl && (
              <button
                onClick={genAlts}
                disabled={altsLoading}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-brand-amber text-black font-bold hover:bg-brand-amber/90 transition disabled:opacity-60"
                data-testid={`${testIdBase}-swap-btn`}
              >
                {altsLoading
                  ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Painting 3 options… ~30s</>)
                  : (<><Sparkles className="h-3.5 w-3.5" /> Swap image (3 AI options)</>)}
              </button>
            )}
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-white/25 text-white/85 hover:border-white hover:text-white transition"
              data-testid={`${testIdBase}-lightbox-close`}
            >
              <X className="h-3.5 w-3.5" /> Close
            </button>
          </div>
        </div>

        {uLoading && (
          <div className="mt-4 flex items-center gap-2 text-white/70 text-xs" data-testid={`${testIdBase}-unsplash-loading`}>
            <Loader2 className="h-4 w-4 animate-spin" /> Fetching live results from Unsplash…
          </div>
        )}

        {!uLoading && uOptions && (
          <div className="mt-4" data-testid={`${testIdBase}-unsplash-grid`}>
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
                // Unsplash results for "{uQuery}" — click to use
              </div>
              <form
                className="flex items-center gap-1.5"
                onSubmit={(e) => { e.preventDefault(); searchUnsplash(e.target.elements.uq.value); }}
              >
                <input
                  name="uq" defaultValue={uQuery} placeholder="Search Unsplash…"
                  className="bg-white/10 border border-white/20 rounded-md px-2.5 py-1.5 text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-sky-400 w-44"
                  data-testid={`${testIdBase}-unsplash-search-input`}
                />
                <button type="submit" className="p-1.5 rounded-md bg-white/10 border border-white/20 text-white/80 hover:text-white transition" data-testid={`${testIdBase}-unsplash-search-btn`}>
                  <Search className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
            {uOptions.length === 0 ? (
              <div className="text-xs text-white/60">No results — try another search term.</div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {uOptions.map((o, i) => (
                  <button
                    key={o.image_url}
                    onClick={() => pickUnsplash(o)}
                    disabled={!!uSelecting}
                    className="group/u relative rounded-lg overflow-hidden border border-white/15 hover:border-sky-400 transition focus:outline-none disabled:opacity-70"
                    data-testid={`${testIdBase}-unsplash-opt-${i}`}
                    title={`Photo by ${o.photographer} on Unsplash`}
                  >
                    <img src={o.thumb_url} alt={o.alt} className={`w-full ${altAspectClass} object-cover`} />
                    <span className="absolute inset-x-0 bottom-0 bg-black/75 text-[10px] text-white/90 py-1 px-1.5 text-left truncate">
                      {uSelecting === o.image_url ? "Setting…" : <>📷 {o.photographer}</>}
                    </span>
                    <span className="absolute inset-0 grid place-items-center bg-black/0 group-hover/u:bg-black/45 transition">
                      <span className="opacity-0 group-hover/u:opacity-100 transition text-[11px] font-bold bg-sky-500/95 text-white px-2.5 py-1 rounded-md">
                        {uSelecting === o.image_url ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Use this"}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 text-[11px] text-white/60">
              Photos from <a href="https://unsplash.com/?utm_source=broadcastflow_ai&utm_medium=referral" target="_blank" rel="noreferrer" className="underline hover:text-white/90">Unsplash</a> — photographer credit is added automatically wherever the photo is used.
            </div>
          </div>
        )}

        {alts && alts.length > 0 && (
          <div className="mt-4" data-testid={`${testIdBase}-alts`}>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-2">
              // Pick your favorite — click to set
            </div>
            <div className="grid grid-cols-3 gap-3">
              {alts.map((a) => (
                <button
                  key={a.alt_id}
                  onClick={() => pickAlt(a.alt_id)}
                  disabled={!!selecting}
                  className="group/alt relative rounded-lg overflow-hidden border border-white/15 hover:border-brand-amber transition focus:outline-none disabled:opacity-70"
                  data-testid={`${testIdBase}-alt-${a.alt_id}`}
                  title={`Use this ${a.slug} option`}
                >
                  <img src={a.image_url} alt={a.slug} className={`w-full ${altAspectClass} object-cover`} />
                  <span className="absolute inset-x-0 bottom-0 bg-black/70 text-[10px] text-white/80 py-1 text-center capitalize">
                    {selecting === a.alt_id ? "Setting…" : a.slug}
                  </span>
                  <span className="absolute inset-0 grid place-items-center bg-black/0 group-hover/alt:bg-black/45 transition">
                    <span className="opacity-0 group-hover/alt:opacity-100 transition text-[11px] font-bold bg-brand-amber/90 text-black px-2.5 py-1 rounded-md">
                      {selecting === a.alt_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Use this"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
